import React, { useCallback, useRef, useState } from "react";
import { useAppDispatch, useAppSelector } from "@store/index";
import {
  chartConfigSelector,
  chartConfigDispatcher,
} from "@store/slices/chart-config-slice";
import { useGetDatasetQuestionsResponseMutation } from "@store/api/dataset-api";
import {
  significanceLevelToPValue,
  testTypeToApiFormat,
} from "@utils/statistical-testing-utils";
import { resolveVisualizationBaseFilters } from "@utils/visualization-base-search";
import { mergeBaseFiltersWithExistingFilters } from "@utils/visualization-base-utils";
import { processGroupedBarChartData } from "@utils/grouped-bar-utils";
import type { WidgetRollup } from "@models/Rollup";
import type { VisualizationBase } from "@models/VisualizationBase";
import type { WeightSelection } from "../../bar-chart-config/hooks/useBarChartData";

const WEIGHT_SELECTION_MAP: Record<string, WeightSelection> = {
  DISABLED: "DISABLED",
  DATASET_DEFAULT: "DATASET_DEFAULT",
  SURVEY_WEIGHT: "SURVEY_WEIGHT",
};

interface UseGroupedBarChartDataReturn {
  fetchChartData: (
    weight: WeightSelection,
    shouldApplyFilters: boolean,
    overrideDates?: { startDate: string | null; endDate: string | null },
    overrideStatTestingEnabled?: boolean,
    overridePeriodCount?: number | null,
    preserveExistingStatSig?: boolean,
    // Kept for signature parity with the bar chart handlers; grouped bar has no
    // rollups or single-entity focus, so these are ignored.
    overrideRollup?: WidgetRollup | null,
    overrideFocusParams?: { focusMode: string; focusEntity: string },
    overrideCategoryRollups?: WidgetRollup[] | null,
    overrideVisualizationBase?: VisualizationBase | null,
  ) => Promise<void>;
  isRefetchingData: boolean;
  weightsApplied: boolean;
  setWeightsApplied: React.Dispatch<React.SetStateAction<boolean>>;
  getDatasetQuestionsResponse: ReturnType<
    typeof useGetDatasetQuestionsResponseMutation
  >[0];
}

/**
 * Grouped bar data hook. Mirrors useBarChartData's request handling (weight,
 * filters, dates, rolling average, statistical testing, visualization base) but
 * sends chartType GROUPED_BAR with only a focus level (VARIABLE | VALUE) — no
 * focusEntity, no rollups — and keeps the full descriptiveMetrics[] via the
 * grouped transform.
 */
export function useGroupedBarChartData(): UseGroupedBarChartDataReturn {
  const dispatch = useAppDispatch();
  const { currentConfig, datasetId, questionId, activeFilters } =
    useAppSelector(chartConfigSelector);

  const [weightsApplied, setWeightsApplied] = useState<boolean>(true);

  const [getDatasetQuestionsResponse, { isLoading: isRefetchingData }] =
    useGetDatasetQuestionsResponseMutation();

  const {
    updateChartData,
    clearRollingAverageResponseFields,
    setGroupedChartData,
  } = chartConfigDispatcher(dispatch);

  const rollingAverageRef = useRef(currentConfig.rollingAverage);
  rollingAverageRef.current = currentConfig.rollingAverage;

  const fetchChartData = useCallback(
    async (
      weight: WeightSelection,
      shouldApplyFilters: boolean,
      overrideDates?: { startDate: string | null; endDate: string | null },
      overrideStatTestingEnabled?: boolean,
      overridePeriodCount?: number | null,
      _preserveExistingStatSig?: boolean,
      _overrideRollup?: WidgetRollup | null,
      overrideFocusParams?: { focusMode: string; focusEntity: string },
      _overrideCategoryRollups?: WidgetRollup[] | null,
      overrideVisualizationBase?: VisualizationBase | null,
    ) => {
      if (!datasetId || !questionId) return;

      let startDate: string | null | undefined =
        overrideDates !== undefined
          ? overrideDates.startDate
          : currentConfig.timePeriodSelection?.startDate;
      let endDate: string | null | undefined =
        overrideDates !== undefined
          ? overrideDates.endDate
          : currentConfig.timePeriodSelection?.endDate;

      const statTestingEnabled =
        overrideStatTestingEnabled !== undefined
          ? overrideStatTestingEnabled
          : currentConfig.statisticalTesting?.enabled;

      const explicitNoDates =
        overrideDates !== undefined &&
        !overrideDates.startDate &&
        !overrideDates.endDate;
      const reduxIsAllTime =
        currentConfig.timePeriodSelection?.timePeriod === "all_time";
      const skipDates =
        explicitNoDates || (overrideDates === undefined && reduxIsAllTime);

      if (!skipDates && !startDate && !endDate) {
        if (currentConfig.responsesMinDate && currentConfig.responsesMaxDate) {
          startDate = currentConfig.responsesMinDate;
          endDate = currentConfig.responsesMaxDate;
        }
      }

      const shouldIncludeDates = !skipDates && !!(startDate && endDate);

      clearRollingAverageResponseFields();

      try {
        const effectiveFocusMode =
          overrideFocusParams?.focusMode ?? currentConfig.focusMode ?? "VARIABLE";
        const effectiveVisualizationBase =
          overrideVisualizationBase !== undefined
            ? overrideVisualizationBase
            : currentConfig.visualizationBase;
        const baseFilters = await resolveVisualizationBaseFilters(
          dispatch,
          datasetId,
          effectiveVisualizationBase,
        );
        const dashboardFilters =
          shouldApplyFilters && activeFilters.length > 0 ? activeFilters : [];
        const combinedFilters = mergeBaseFiltersWithExistingFilters(
          dashboardFilters,
          baseFilters,
        );

        const result = await getDatasetQuestionsResponse({
          id: questionId,
          datasetId: datasetId,
          topN: null,
          sorting: "ORIGINAL",
          weightSelection: WEIGHT_SELECTION_MAP[weight],
          chartType: "GROUPED_BAR",
          filters: combinedFilters.length > 0 ? combinedFilters : undefined,
          focusMode: effectiveFocusMode,
          ...(shouldIncludeDates && {
            startDate: startDate!,
            endDate: endDate!,
          }),
          ...(statTestingEnabled &&
            shouldIncludeDates && {
              enableStatTesting: true,
              statTestingType: testTypeToApiFormat(
                currentConfig.statisticalTesting?.testType,
              ),
              pValue: significanceLevelToPValue(
                currentConfig.statisticalTesting?.significanceLevel || "90%",
              ),
              minimumSampleSize:
                currentConfig.statisticalTesting?.minimumSampleSize || 30,
            }),
          ...(() => {
            if (skipDates) return {};
            const ra = rollingAverageRef.current;
            const pc =
              overridePeriodCount !== undefined
                ? overridePeriodCount
                : ra?.enabled
                  ? ra.windowSize
                  : null;
            return pc ? { periodCount: pc } : {};
          })(),
        });

        if (result.data?.data && Array.isArray(result.data.data)) {
          const grouped = processGroupedBarChartData(result.data.data);

          // Store the id-carrying structure for render-time reconciliation
          // (selection, visibility, custom order and label overrides).
          setGroupedChartData(grouped);

          // Generic categories/series (label-based) keep the shared availability
          // and time-period checks working across bar + grouped bar.
          const categories = grouped.categories.map((c) => c.label);
          const series = grouped.series.map((s) => ({
            name: s.label,
            variableId: s.id,
            data: s.data.map((cell) =>
              typeof cell === "number" ? cell : cell.value,
            ),
          }));

          // Chart-level N: max baseN across items, mirroring the bar chart.
          const baseNValues = (result.data.data as any[])
            .map((item: any) => item.surveys?.[0]?.descriptiveMetrics?.[0]?.baseN)
            .filter((n: any): n is number => typeof n === "number");
          const chartN: number | null =
            baseNValues.length > 0
              ? Math.max(...baseNValues)
              : (result.data?.question?.responseCount ?? null);

          updateChartData({
            categories,
            series,
            responsesMinDate: result.data?.responsesMinDate,
            responsesMaxDate: result.data?.responsesMaxDate,
            effectiveRollingPeriodCount:
              result.data?.effectiveRollingPeriodCount,
            rollingPeriodsWarningMessage:
              result.data?.rollingPeriodsWarningMessage,
            comparisonRollingLabel: result.data?.comparisonRollingLabel,
            missingRollingPeriodLabels: result.data?.missingRollingPeriodLabels,
            chartN,
          });

          if (result.data?.weightsApplied !== undefined) {
            setWeightsApplied(result.data.weightsApplied);
          }
        }
      } catch (error) {
        console.error("Error fetching grouped bar chart data:", error);
      }
    },
    [
      dispatch,
      datasetId,
      questionId,
      activeFilters,
      currentConfig.statisticalTesting,
      currentConfig.timePeriodSelection,
      currentConfig.responsesMinDate,
      currentConfig.responsesMaxDate,
      currentConfig.focusMode,
      currentConfig.visualizationBase,
      getDatasetQuestionsResponse,
      updateChartData,
      clearRollingAverageResponseFields,
      setGroupedChartData,
    ],
  );

  return {
    fetchChartData,
    isRefetchingData,
    weightsApplied,
    setWeightsApplied,
    getDatasetQuestionsResponse,
  };
}
